// Incoming appointment requests, with approve and deny.
//
// ── Approve is two-phase, and must stay that way ───────────────────────────
// Approving opens AddPatientModal so the notes-c row is created FROM THE
// ADMIN'S OWN SESSION. That is what makes the set_notes_c_timezone() trigger
// stamp the approving user's timezone onto the appointment; a service-role
// insert from the backend would stamp nothing. Only once AddPatientModal hands
// back the inserted row do we call approveRequest with its id.
//
// Closing the modal without saving therefore does nothing at all: no row, no
// row id, no approval, and the request stays pending. The rule that an
// incomplete approval must not go through is enforced by that missing id
// rather than by a flag that could drift out of step with reality.
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import PhoneStatusBadge from "../sms/PhoneStatusBadge";
import AddPatientModal from "../AddPatientModal";
import {
  approveRequest,
  denyRequest,
  fetchConfig,
  fetchRequests,
} from "../../utils/appointmentsApi";

const STATUS_TONE = {
  pending: "yellow",
  approved: "green",
  denied: "red",
  cancelled: "gray",
  // Nobody acted before the slot passed. Distinct from denied — the clinic
  // didn't refuse it, it just lapsed.
  expired: "orange",
};

const hhmm = (t) => String(t || "").slice(0, 5);
const ymd = (d) => String(d || "").slice(0, 10);

export default function RequestsTab({ schema }) {
  const toast = useToast();
  const [requests, setRequests] = useState(null);
  // "active" = pending/approved/denied for a date that hasn't gone. Cancelled
  // and expired requests, and anything whose date has passed, are history and
  // sit behind View all rather than filling the working inbox.
  const [status, setStatus] = useState("active");
  const [needsPhysician, setNeedsPhysician] = useState(false);
  const [approving, setApproving] = useState(null); // request whose modal is open
  const [denying, setDenying] = useState(null);
  const [denyReason, setDenyReason] = useState("");
  const [blockScope, setBlockScope] = useState("date");
  const [error, setError] = useState(null);

  const reload = useCallback(() => {
    fetchRequests({ schema, status })
      .then(setRequests)
      .catch((e) => {
        setRequests([]);
        setError(e.message);
      });
  }, [schema, status]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    fetchConfig(schema)
      .then((c) => setNeedsPhysician(!!c.needs_physician_name))
      .catch(() => setNeedsPhysician(false));
  }, [schema]);

  const openDeny = (request) => {
    setDenying(request);
    setDenyReason("");
    // Blocking just the requested date and time is the default: widening it to
    // every week is a bigger decision than declining one appointment.
    setBlockScope("date");
  };

  const confirmDeny = async () => {
    try {
      const res = await denyRequest(denying.id, {
        schema,
        reason: denyReason,
        block: blockScope,
      });
      if (res.warning) {
        // The denial went through but the block did not. Saying "done" here
        // would leave the slot quietly open for the next patient.
        toast({ status: "warning", title: res.warning, duration: 9000 });
      }
      setDenying(null);
      reload();
    } catch (e) {
      if (e.code === "not_pending") {
        toast({ status: "info", title: "That request was already decided." });
        setDenying(null);
        reload();
      } else {
        toast({ status: "error", title: e.message });
      }
    }
  };

  return (
    <Box>
      {needsPhysician && (
        <Alert status="warning" mb={4} borderRadius="md">
          <AlertIcon />
          <Text>
            No <strong>physician name</strong> is set for {schema}, so patients
            cannot see this doctor when requesting an appointment. Set
            <strong> physician_name</strong> in schema settings.
          </Text>
        </Alert>
      )}

      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Flex mb={4} align="center" gap={3}>
        <Text fontWeight="bold">Requests</Text>
        <Select
          size="sm"
          width="180px"
          aria-label="Filter by status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="active">Active</option>
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="denied">Denied</option>
          <option value="expired">Expired</option>
        </Select>
        {status !== "all" && (
          <Button size="sm" variant="outline" onClick={() => setStatus("all")}>
            View all
          </Button>
        )}
      </Flex>

      {requests === null && <Spinner />}

      {requests && requests.length === 0 && (
        <Text color="gray.500">
          {status === "all"
            ? "No requests yet."
            : status === "active"
              ? "No active requests — nothing waiting on you."
              : `No ${status} requests.`}
        </Text>
      )}

      {requests && requests.length > 0 && (
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Patient</Th>
              <Th>When</Th>
              <Th>About</Th>
              <Th>Status</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {requests.map((r) => (
              <Tr key={r.id}>
                <Td>
                  <Text fontWeight="semibold">{r.patient_name}</Text>
                  <Text fontSize="xs" color="gray.500">
                    {r.patient_dob ? ymd(r.patient_dob) : "no dob"}
                    {r.patient_phone ? ` · ${r.patient_phone}` : ""}
                    {r.patient_phone && <PhoneStatusBadge phone={r.patient_phone} compact ml={1} />}
                  </Text>
                </Td>
                <Td>
                  {ymd(r.slot_date)}
                  <br />
                  {hhmm(r.slot_time)}
                </Td>
                <Td maxW="240px">
                  <Text fontSize="xs" noOfLines={3}>
                    {r.note || "—"}
                  </Text>
                </Td>
                <Td>
                  <Badge colorScheme={STATUS_TONE[r.status] || "gray"}>
                    {r.status}
                  </Badge>
                  {r.status === "denied" && r.denial_reason && (
                    <Text fontSize="xs" color="gray.500">
                      {r.denial_reason}
                    </Text>
                  )}
                </Td>
                <Td>
                  {r.status === "pending" && (
                    <HStack spacing={2}>
                      <Button
                        size="xs"
                        colorScheme="green"
                        onClick={() => setApproving(r)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => openDeny(r)}
                      >
                        Deny
                      </Button>
                    </HStack>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {/* Phase 1 of approval — the admin completes and saves the appointment.
          `type` is deliberately NOT prefilled: filling it in is what makes
          saving a conscious act rather than a rubber stamp. */}
      {approving && (
        <AddPatientModal
          isOpen
          onClose={() => setApproving(null)} // no save, no approval
          onAddPatient={async (entry) => {
            const target = approving;
            setApproving(null);
            try {
              // Phase 2 — link the row that was just created.
              await approveRequest(target.id, {
                schema,
                notesCRowId: entry?.id,
              });
              toast({ status: "success", title: "Appointment approved" });
            } catch (e) {
              toast({
                status: "error",
                title:
                  e.code === "not_pending"
                    ? "That request was already decided — the appointment was still saved."
                    : e.message,
                duration: 9000,
              });
            }
            reload();
          }}
          initialData={{
            date_: ymd(approving.slot_date),
            time_: hhmm(approving.slot_time),
            patient_name: approving.patient_name || "",
            dob: approving.patient_dob ? ymd(approving.patient_dob) : "",
            tel_num: approving.patient_phone || "",
            email: approving.patient_email || "",
            EHR_ID: approving.patient_ehr_id || "",
            physician_name: approving.physician_name || "",
          }}
        />
      )}

      {denying && (
        <Modal isOpen onClose={() => setDenying(null)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Deny this request</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={2}>
                {denying.patient_name} — {ymd(denying.slot_date)}{" "}
                {hhmm(denying.slot_time)}
              </Text>
              <Textarea
                placeholder="Reason (shown to the patient)"
                aria-label="Denial reason"
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                mb={4}
              />
              <Text fontWeight="semibold" mb={2}>
                Also make this time unavailable?
              </Text>
              <RadioGroup value={blockScope} onChange={setBlockScope}>
                <Stack>
                  <Radio value="date">
                    Just {ymd(denying.slot_date)} at {hhmm(denying.slot_time)}
                  </Radio>
                  <Radio value="weekday">
                    Every week at {hhmm(denying.slot_time)} from now on
                  </Radio>
                  <Radio value="none">
                    No — leave it open for other patients
                  </Radio>
                </Stack>
              </RadioGroup>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setDenying(null)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDeny}>
                Deny request
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}
